/**
 * Order Detail Page - Premium UI/UX Design
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './OrderDetailPage.css';
import { ordersAPI, documentsAPI, companyProfileAPI, isAuthenticated } from '../../services/api';
import { useToast } from '../../Components/Toast/Toast';

// Status configurations
const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: '#f59e0b',
    bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    icon: '⏳',
  },
  processing: {
    label: 'Processing',
    color: '#3b82f6',
    bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    icon: '⚙️',
  },
  completed: {
    label: 'Completed',
    color: '#10b981',
    bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    icon: '✓',
  },
  failed: {
    label: 'Failed',
    color: '#ef4444',
    bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    icon: '✕',
  },
  cancelled: {
    label: 'Cancelled',
    color: '#6b7280',
    bg: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
    icon: '⊘',
  },
};

// Loading Skeleton
const DetailSkeleton = () => (
  <div className="detail-skeleton">
    <div className="skeleton-header-block">
      <div className="skeleton-line w-50"></div>
      <div className="skeleton-line w-30"></div>
    </div>
    <div className="skeleton-cards">
      <div className="skeleton-card"></div>
      <div className="skeleton-card"></div>
    </div>
  </div>
);

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [order, setOrder] = useState(null);
  console.log(order, "order");
  const [documents, setDocuments] = useState({ inputDocuments: [], outputDocuments: [] });
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Timer state for processing elapsed time
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [totalElapsedSeconds, setTotalElapsedSeconds] = useState(0);

  // Auto-scroll to top when page first loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Prevent duplicate API calls
  const hasFetched = useRef(false);

  // Fetch order details - only once on mount
  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    // Prevent duplicate calls
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const orderResponse = await ordersAPI.getOrderById(orderId);
        if (orderResponse.success) {
          setOrder(orderResponse.data.order);
        } else {
          toast.error(orderResponse.message || 'Failed to fetch order');
          return;
        }

        const docsResponse = await documentsAPI.getOrderDocuments(orderId);
        if (docsResponse.success) {
          setDocuments({
            inputDocuments: docsResponse.data.inputDocuments || [],
            outputDocuments: docsResponse.data.outputDocuments || [],
          });
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        toast.error('Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, navigate]);

  const refreshData = async () => {
    try {
      const orderResponse = await ordersAPI.getOrderById(orderId);
      if (orderResponse.success) {
        setOrder(orderResponse.data.order);
      }

      const docsResponse = await documentsAPI.getOrderDocuments(orderId);
      if (docsResponse.success) {
        setDocuments({
          inputDocuments: docsResponse.data.inputDocuments || [],
          outputDocuments: docsResponse.data.outputDocuments || [],
        });
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  // Timer for order processing
  useEffect(() => {
    if (!order || order.status !== 'processing' || !order.processingStartedAt) {
      setElapsedSeconds(0);
      setTotalElapsedSeconds(0);
      return;
    }

    const startTime = new Date(order.metadata?.currentFileStartedAt || order.processingStartedAt).getTime();
    const totalStartTime = new Date(order.processingStartedAt).getTime();

    const tick = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));
      setTotalElapsedSeconds(Math.max(0, Math.floor((Date.now() - totalStartTime) / 1000)));
    };

    tick();
    const timerId = setInterval(tick, 1000);
    return () => clearInterval(timerId);
  }, [order]);  // eslint-disable-line react-hooks/exhaustive-deps -- recalculates from processingStartedAt on every order update

  // Poll for live status and document updates
  useEffect(() => {
    if (!order || !['pending', 'processing'].includes(order.status)) {
      return;
    }

    const currentStatus = order.status;

    const pollInterval = setInterval(async () => {
      try {
        const orderResponse = await ordersAPI.getOrderById(orderId);
        if (orderResponse.success) {
          const updatedOrder = orderResponse.data.order;

          // Update order and continuously fetch documents to show progress
          if (updatedOrder.status === 'processing' || updatedOrder.status !== order.status) {
            setOrder(updatedOrder);

            const docsResponse = await documentsAPI.getOrderDocuments(orderId);
            if (docsResponse.success) {
              setDocuments({
                inputDocuments: docsResponse.data.inputDocuments || [],
                outputDocuments: docsResponse.data.outputDocuments || [],
              });
            }
          }

          if (updatedOrder.status !== currentStatus) {
            if (updatedOrder.status === 'processing') {
              toast.info('Order is now being processed...');
            } else if (updatedOrder.status === 'completed') {
              toast.success('Order processing completed!');
            } else if (updatedOrder.status === 'failed') {
              toast.error('Order processing failed. Please check the details.');
            }
          }
        }
      } catch (error) {
        console.error('Error polling order status:', error);
      }
    }, order?.status === 'processing' ? 2000 : 5000); // Poll faster (2s) during processing for smooth updates

    return () => clearInterval(pollInterval);
    // 'order' is intentionally omitted from deps: including it would restart the interval on
    // every poll response (order updates every 2s), causing a circular restart loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status, orderId, toast]);



  const handleDownload = async (documentId, originalName) => {
    setDownloading(prev => ({ ...prev, [documentId]: true }));

    try {
      const blob = await documentsAPI.downloadDocument(documentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = originalName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${originalName}`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloading(prev => ({ ...prev, [documentId]: false }));
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    setActiveDropdown(null);
    try {
      // Map service slug to API service type
      const slugToType = {
        'board-meeting-minutes': 'board_meeting',
        'committee-meeting-minutes': 'committee',
        'agm-minutes': 'agm',
        'egm-minutes': 'egm',
      };
      const serviceSlug = order?.items?.[0]?.serviceSlug || 'board-meeting-minutes';
      const serviceType = slugToType[serviceSlug] || 'board_meeting';
      const blob = await companyProfileAPI.downloadTemplate(serviceType);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Meeting_Template_Prepopulated.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded pre-populated template');
    } catch (error) {
      console.error('Template download error:', error);
      if (error.message?.includes('No company profile')) {
        toast.error('Please upload a document first to enable pre-populated templates');
      } else {
        toast.error(error.message || 'Failed to download template');
      }
    } finally {
      setDownloadingTemplate(false);
    }
  };

  const toggleDropdown = (docId) => {
    setActiveDropdown(activeDropdown === docId ? null : docId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.download-dropdown-container')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSubmitForProcessing = async () => {
    setSubmitting(true);
    try {
      // Check if order documents are complete and set ready flag
      const checkResponse = await documentsAPI.checkOrderComplete(order._id);
      console.log(checkResponse, "checkResponse");
      
      if (!checkResponse.success) {
        toast.error('Failed to check document status');
        return;
      }

      if (!checkResponse.data.isComplete) {
        toast.error('Please upload all required documents before submitting');
        return;
      }

      // Documents are complete and flag is set, proceed with submission
      await documentsAPI.submitOrder(order._id);
      setOrder(prev => ({ ...prev, status: 'processing' }));
      toast.success('Order submitted for processing!');
      await refreshData();
    } catch (err) {
      toast.error('Failed to submit order: ' + (err.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDocumentUpdate = async (documentType, document, action) => {
    try {
      if (action === 'delete') {
        // Delete existing document from backend
        await documentsAPI.deleteDocument(document._id);
        toast.success('Document deleted successfully');
      } else if (action === 'add') {
        // Upload new document
        const formData = new FormData();
        formData.append('file', document);
        formData.append('documentType', documentType);
        formData.append('orderId', order._id);
        
        await documentsAPI.uploadDocument(formData);
        toast.success('Document uploaded successfully');
      }
      
      // Refresh documents list
      await refreshData();
    } catch (error) {
      console.error('Document update error:', error);
      toast.error(`Failed to ${action} document`);
    }
  };

  const handleCancelOrder = async () => {
    try {
      await ordersAPI.cancelOrder(order._id);
      toast.success('Order cancelled');
      await refreshData();
    } catch (err) {
      toast.error('Failed to cancel order');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 KB';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('word') || mimeType?.includes('document')) return '📝';
    if (mimeType?.includes('sheet') || mimeType?.includes('excel')) return '📊';
    if (mimeType?.includes('image')) return '🖼️';
    return '📁';
  };

  if (loading) {
    return (
      <div className="order-detail-page">
        <div className="detail-container">
          <DetailSkeleton />
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-detail-page">
        <div className="detail-container">
          <div className="error-state">
            <div className="error-icon">😕</div>
            <h2>Order Not Found</h2>
            <p>We couldn't find the order you're looking for.</p>
            <button onClick={() => navigate('/orders')} className="btn-primary">
              Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;

  return (
    <div className="order-detail-page">
      {/* Hero Header */}
      <div className="detail-hero" style={{ background: status.bg }}>
        <div className="hero-content">
          <button onClick={() => navigate('/orders')} className="back-link">
            <span>←</span> Back to Orders
          </button>

          <div className="order-header">
            <div className="order-title-section">
              <span className="order-label">Order</span>
              <h1 className="order-id">{order.orderNumber}</h1>
              <p className="order-date-text">Placed on {formatDate(order.createdAt)}</p>
            </div>

            <div className={`status-badge ${status.label.toLowerCase()}`}>
              <span className="status-icon">{status.icon}</span>
              <span className="status-text">{status.label}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-container">
        {/* Summary Cards */}
        <div className="summary-grid">
          <div className="summary-card">
            <div className="card-icon">💰</div>
            <div className="card-content">
              <span className="card-label">Total Amount</span>
              <span className="card-value amount">{formatPrice(order.totalAmount)}</span>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">💳</div>
            <div className="card-content">
              <span className="card-label">Payment Status</span>
              <span className={`card-value payment-${order.paymentStatus}`}>
                {order.paymentStatus === 'paid' ? '✓ Paid' :
                  order.paymentStatus === 'awaiting_payment' ? 'Awaiting Payment' : order.paymentStatus}
              </span>
            </div>
          </div>

          <div className="summary-card">
            <div className="card-icon">📦</div>
            <div className="card-content">
              <span className="card-label">Services</span>
              <span className="card-value">{(order.items || []).length} service(s)</span>
            </div>
          </div>

          {order.completedAt && (
            <div className="summary-card success">
              <div className="card-icon">✅</div>
              <div className="card-content">
                <span className="card-label">Completed On</span>
                <span className="card-value">{new Date(order.completedAt).toLocaleDateString('en-IN')}</span>
              </div>
            </div>
          )}

          {(order.status === 'processing' || (order.completedAt && order.processingStartedAt)) && (() => {
            const totalSec = order.status === 'completed'
              ? Math.floor((new Date(order.completedAt) - new Date(order.processingStartedAt)) / 1000)
              : totalElapsedSeconds;
            const mins = Math.floor(totalSec / 60);
            const secs = totalSec % 60;
            const label = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
            const icon = order.status === 'completed' ? '⚡' : '⏱';
            return (
              <div className={`summary-card${order.status === 'completed' ? ' success' : ' processing'}`}>
                <div className="card-icon">{icon}</div>
                <div className="card-content">
                  <span className="card-label">Total Processing Time</span>
                  <span className="card-value">{label}</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Processing Notes */}
        {order.processingNotes && (
          <div className="notes-banner">
            <span className="notes-icon">📝</span>
            <div className="notes-content">
              <span className="notes-label">Processing Notes</span>
              <p>{order.processingNotes}</p>
            </div>
          </div>
        )}

        {/* Services Section */}
        <div className="section-card">
          <div className="section-header">
            <h2>🛍️ Services Ordered</h2>
          </div>
          <div className="services-list">
            {(order.items || []).map((item, index) => (
              <div key={index} className="service-row">
                <div className="service-info">
                  <h3>{item.service?.name || item.serviceName}</h3>
                  <p>{item.service?.shortDescription || 'Document processing service'}</p>
                </div>
                <div className="service-pricing">
                  <span className="price">{formatPrice(item.price)}</span>
                  <span className="quantity">× {item.quantity}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Documents Section */}
        <div className="documents-section">
          {/* Uploaded Documents */}
          <div className="section-card">
            <div className="section-header">
              <h2>📤 Uploaded Documents</h2>
              <span className="doc-count">{documents.inputDocuments.length} file(s)</span>
            </div>

            {documents.inputDocuments.length === 0 ? (
              <div className="no-docs">
                <div className="no-docs-icon">📁</div>
                <p>No documents uploaded yet</p>
                {order.status === 'pending' && (
                  <button
                    onClick={() => navigate('/upload-documents', {
                      state: { orderId: order._id, order, services: (order.items || []).map(item => item.service).filter(Boolean) }
                    })}
                    className="btn-upload-docs"
                  >
                    <span>📤</span> Upload Documents
                  </button>
                )}
              </div>
            ) : (
              <div className="docs-grid">
                {documents.inputDocuments.map((doc) => (
                  <div key={doc._id} className="doc-card">
                    <div className="doc-icon">{getFileIcon(doc.mimeType)}</div>
                    <div className="doc-info">
                      <h4>{doc.originalName}</h4>
                      <div className="doc-meta">
                        <span className="doc-slot">{doc.documentSlot || 'Document'}</span>
                        <span className="doc-size">{formatFileSize(doc.fileSize)}</span>
                      </div>
                    </div>
                    <div className="download-dropdown-container">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleDropdown(doc._id); }}
                        className="doc-download-btn"
                        disabled={downloading[doc._id] || downloadingTemplate}
                      >
                        {(downloading[doc._id] || downloadingTemplate) ? (
                          <span className="spinner"></span>
                        ) : (
                          <span>⬇️</span>
                        )}
                      </button>
                      {activeDropdown === doc._id && (
                        <div className="download-dropdown-menu">
                          <button
                            className="dropdown-item"
                            onClick={() => { setActiveDropdown(null); handleDownload(doc._id, doc.originalName); }}
                          >
                            📄 Download Original
                          </button>
                          <button
                            className="dropdown-item"
                            onClick={handleDownloadTemplate}
                            disabled={downloadingTemplate}
                          >
                            ✨ Download Pre-populated Template
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Generated Documents */}
          <div className="section-card output-section">
            <div className="section-header">
              <h2>📥 Generated Documents</h2>
              <span className="doc-count success">{documents.outputDocuments.length} file(s)</span>
            </div>

            {order.status === 'pending' && documents.outputDocuments.length === 0 ? (
              <div className="no-docs output">
                <div className="no-docs-icon">⏳</div>
                <p>Documents will be generated after processing</p>
              </div>
            ) : (
              <div className="output-content-container">
                {order.status === 'processing' && (
                  <div className="processing-progress-container" style={{ marginBottom: documents.outputDocuments.length > 0 ? '24px' : '0' }}>
                    {/* Circular Progress Indicator */}
                    <div className="progress-circle">
                      <svg viewBox="0 0 100 100">
                        <circle className="progress-bg" cx="50" cy="50" r="45" />
                        <circle
                          className="progress-bar"
                          cx="50" cy="50" r="45"
                          style={{
                            strokeDasharray: `${(order.processingProgress || 0) * 2.83} 283`
                          }}
                        />
                      </svg>
                      <div className="progress-text">
                        <span className="progress-percent">{order.processingProgress || 0}%</span>
                      </div>
                    </div>

                    {/* Stage Info */}
                    <div className="progress-info">
                      <p className="stage-label">
                        {order.metadata?.totalFilesCount > 1 && order.metadata?.processedFilesCount !== undefined ?
                          `Processing file ${Math.min(order.metadata.processedFilesCount + 1, order.metadata.totalFilesCount)} of ${order.metadata.totalFilesCount}: ` : ''}
                        {order.processingStageLabel || 'Processing...'}
                      </p>

                      {/* Elapsed Timer for Current File */}
                      <div className="timer-row">
                        <span className={`time-remaining${elapsedSeconds > 120 ? ' overtime' : ''}`}>
                          ⏱ {Math.floor(elapsedSeconds / 60)}:{String(elapsedSeconds % 60).padStart(2, '0')} elapsed for this file
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {documents.outputDocuments.length > 0 && (
                  <div className="docs-grid output-grid">
                    {documents.outputDocuments.map((doc) => (
                      <div key={doc._id} className="doc-card output-doc">
                        <div className="doc-icon large">{getFileIcon(doc.mimeType)}</div>
                        <div className="doc-info">
                          <h4>{doc.originalName}</h4>
                          <div className="doc-meta">
                            <span className="doc-slot">{doc.documentSlot || 'Generated'}</span>
                            <span className="doc-size">{formatFileSize(doc.fileSize)}</span>
                          </div>
                          {doc.metadata?.sourceFile && (
                            <span className="source-file-label" title={`Generated from: ${doc.metadata.sourceFile}`}>
                              {doc.metadata.sourceFile}
                            </span>
                          )}
                          <span className="generated-time">
                            Generated {new Date(doc.createdAt).toLocaleDateString('en-IN')}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDownload(doc._id, doc.originalName)}
                          className="doc-download-btn primary"
                          disabled={downloading[doc._id]}
                        >
                          {downloading[doc._id] ? (
                            <span className="spinner"></span>
                          ) : (
                            <>
                              <span>⬇️</span>
                              <span>Download</span>
                            </>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {order.status !== 'processing' && order.status !== 'pending' && documents.outputDocuments.length === 0 && (
                  <div className="no-docs output">
                    <div className="no-docs-icon">📄</div>
                    <p>No output documents available</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action Bar - Contains order action buttons:
            - Submit for Processing: Submits uploaded documents to start AI processing
            - Upload Documents: Navigates to upload page to add required documents
            - Cancel Order: Cancels the current order */}
        {(['pending', 'processing'].includes(order.status)) && (
          <div className="action-bar">
            {order.status === 'pending' && documents.inputDocuments.length > 0 && (
              <button
                onClick={handleSubmitForProcessing}
                className="btn-submit"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner white"></span>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>🚀</span>
                    <span>Submit for Processing</span>
                  </>
                )}
              </button>
            )}

            {order.status === 'pending' && (
              <button
                onClick={() => navigate('/upload-documents', {
                  state: { 
                    orderId: order._id, 
                    order, 
                    services: (order.items || []).map(item => item.service).filter(Boolean),
                    manageExisting: true
                  }
                })}
                className="btn-upload"
              >
                <span>🔄</span>
                <span>{documents.inputDocuments.length > 0 ? 'Change Documents' : 'Upload Documents'}</span>
              </button>
            )}

            {['pending', 'processing'].includes(order.status) && (
              <button onClick={handleCancelOrder} className="btn-cancel">
                Cancel Order
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderDetailPage;
